import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Price, PriceInput, Select } from '@artosera/ui';
import { ETAPES_OUVERTES, formatDate, type EtapeAffaire } from '@artosera/shared';
import {
  pipelineApi,
  useAffaire,
  useActionPipeline,
  type AffairePresentee,
} from '../../api/pipeline.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { MenuDeplacement } from './Deplacement.js';
import { NouvelleAffaireDialog } from './NouvelleAffaireDialog.js';
import styles from './Pipeline.module.css';

export function AffairePanneau({
  id,
  onFermer,
  onDeplacer,
}: {
  id: string;
  onFermer: () => void;
  onDeplacer: (affaire: AffairePresentee, vers: EtapeAffaire) => void;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const fiche = useAffaire(galerieSlug, id);
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={fiche.data?.affaire.numero ?? t('regie:pipeline.affaire')}
      description={fiche.data?.affaire.contactNom}
      closeLabel={t('commun:actions.fermer')}
    >
      <div className={styles.panneau}>
        {fiche.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
        {fiche.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
        {fiche.data ? (
          <ContenuAffaire key={id} affaire={fiche.data.affaire} onDeplacer={onDeplacer} />
        ) : null}
      </div>
    </Dialog>
  );
}
function ContenuAffaire({
  affaire,
  onDeplacer,
}: {
  affaire: AffairePresentee;
  onDeplacer: (affaire: AffairePresentee, vers: EtapeAffaire) => void;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const navigate = useNavigate();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const ecrire = galerieALaction(galerie, 'pipeline:write');
  const ouverte = (ETAPES_OUVERTES as readonly string[]).includes(affaire.etape);
  const [modifier, setModifier] = useState(false);
  const [geste, setGeste] = useState<'offre' | 'reservation' | null>(null);
  const [terme, setTerme] = useState('');
  const [prix, setPrix] = useState(affaire.lignes.map((l) => l.montantCentimes ?? 0));
  const [ligne, setLigne] = useState('0');
  const [vers, setVers] = useState<(typeof ETAPES_OUVERTES)[number]>('interet');
  const rouvrir = useActionPipeline(galerieSlug, (_: void, appel) =>
    pipelineApi.rouvrir(galerieSlug, affaire.id, { vers, version: affaire.version }, appel),
  );
  const cible = useActionPipeline(galerieSlug, (_: void, appel) => {
    if (geste === 'offre')
      return pipelineApi
        .creerOffre(
          galerieSlug,
          affaire.id,
          {
            contactId: affaire.contactId,
            lignes: affaire.lignes.map((l, i) => ({
              oeuvreId: l.oeuvreId,
              exemplaireId: l.exemplaireId ?? undefined,
              prixProposeCentimes: prix[i]!,
            })),
            validiteJusquau: terme,
          },
          appel,
        )
        .then((r) => ({ chemin: `offres/${r.offre.id}` }));
    const l = affaire.lignes[Number(ligne)]!;
    return pipelineApi
      .reserver(
        galerieSlug,
        affaire.id,
        {
          oeuvreId: l.oeuvreId,
          exemplaireId: l.exemplaireId,
          contactId: affaire.contactId,
          jusquau: terme,
          note: affaire.numero,
        },
        appel,
      )
      .then(() => ({
        chemin: `contacts/${encodeURIComponent(affaire.contactId)}`,
      }));
  });
  return (
    <div className={styles.formulaire}>
      <p>{affaire.titre}</p>
      <p>
        {t(`regie:pipeline.etapes.${affaire.etape}`)} · {affaire.probabilite} %
      </p>
      <ul>
        {affaire.lignes.map((l, i) => (
          <LigneAffaire
            key={`${l.oeuvreId}:${l.exemplaireId}`}
            ligne={l}
            numero={i + 1}
            devise={affaire.devise}
          />
        ))}
      </ul>
      {ecrire ? (
        <div className={styles.gestes}>
          <Button variant="secondary" onClick={() => setModifier(true)}>
            {t('regie:pipeline.modifier')}
          </Button>
          {ouverte ? (
            <>
              <MenuDeplacement affaire={affaire} onDeplacer={onDeplacer} />
              <Button
                variant={affaire.propositionPerte ? 'accent' : 'secondary'}
                onClick={() => onDeplacer(affaire, 'perdue')}
              >
                {t('regie:pipeline.marquerPerdue')}
              </Button>
              {galerieAModule(galerie, 'offres-relances') &&
              galerieALaction(galerie, 'offers:write') ? (
                <Button onClick={() => setGeste('offre')}>{t('regie:pipeline.creerOffre')}</Button>
              ) : null}
              {galerieALaction(galerie, 'reservations:write') ? (
                <Button onClick={() => setGeste('reservation')}>
                  {t('regie:pipeline.reserver')}
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <Select
                aria-label={t('regie:pipeline.rouvrirVers')}
                value={vers}
                onValueChange={(v) => setVers(v as typeof vers)}
                options={ETAPES_OUVERTES.map((value) => ({
                  value,
                  label: t(`regie:pipeline.etapes.${value}`),
                }))}
              />
              <Button loading={rouvrir.isPending} onClick={() => rouvrir.mutate()}>
                {t('regie:pipeline.rouvrir')}
              </Button>
            </>
          )}
        </div>
      ) : null}
      {affaire.propositionPerte ? <p>{t('regie:pipeline.propositionPerte')}</p> : null}
      {affaire.motifPerte ? (
        <p>
          {t(`regie:pipeline.motifs.${affaire.motifPerte.code}`)} {affaire.motifPerte.texte}
        </p>
      ) : null}
      {rouvrir.isError ? (
        <p role="alert">
          {estApiError(rouvrir.error) && rouvrir.error.status === 409
            ? t('regie:pipeline.conflit')
            : t('commun:erreurReseau')}
        </p>
      ) : null}
      <h2>{t('regie:pipeline.historique')}</h2>
      <ol>
        {affaire.historique.map((h, i) => (
          <li key={i}>
            {h.de ? `${t(`regie:pipeline.etapes.${h.de}`)} → ` : ''}
            {t(`regie:pipeline.etapes.${h.vers}`)} · {formatDate(new Date(h.le), langue)}{' '}
            {h.automatique ? t('regie:pipeline.automatique') : ''}
          </li>
        ))}
      </ol>
      <nav className={styles.gestes} aria-label={t('regie:pipeline.liens')}>
        {affaire.liens.demandeId ? (
          <Link
            to={`/${galerieSlug}/contacts?onglet=demandes#demande-${encodeURIComponent(affaire.liens.demandeId)}`}
          >
            {t('regie:pipeline.demande')}
          </Link>
        ) : null}
        {affaire.liens.offreIds?.map((id) => (
          <Link key={id} to={`/${galerieSlug}/offres/${encodeURIComponent(id)}`}>
            {t('regie:pipeline.offreLiee')}
          </Link>
        ))}
        {affaire.liens.reservationIds?.map((id) => (
          <Link key={id} to={`/${galerieSlug}/contacts/${encodeURIComponent(affaire.contactId)}`}>
            {t('regie:pipeline.reservationLiee')}
          </Link>
        ))}
        {affaire.liens.factureIds?.map((id) => (
          <Link key={id} to={`/${galerieSlug}/ventes/${encodeURIComponent(id)}`}>
            {t('regie:pipeline.facture')}
          </Link>
        ))}
      </nav>
      {modifier ? (
        <NouvelleAffaireDialog open affaireInitiale={affaire} onOpenChange={setModifier} />
      ) : null}
      {geste ? (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setGeste(null);
          }}
          title={t(geste === 'offre' ? 'regie:pipeline.creerOffre' : 'regie:pipeline.reserver')}
          closeLabel={t('commun:actions.fermer')}
          description={t('regie:pipeline.choisirTerme')}
          footer={
            <Button
              disabled={!terme}
              loading={cible.isPending}
              onClick={() =>
                void cible
                  .mutateAsync()
                  .then((r) => navigate(`/${galerieSlug}/${r.chemin}`))
                  .catch(() => {})
              }
            >
              {t('commun:actions.enregistrer')}
            </Button>
          }
        >
          {geste === 'reservation' ? (
            <Select
              aria-label={t('regie:pipeline.oeuvre')}
              value={ligne}
              onValueChange={setLigne}
              options={affaire.lignes.map((_, i) => ({
                value: String(i),
                label: t('regie:pipeline.ligne', { numero: i + 1 }),
              }))}
            />
          ) : null}
          {geste === 'offre'
            ? affaire.lignes.map((_, i) => (
                <Field
                  key={i}
                  htmlFor={`affaire-offre-prix-${i}`}
                  label={t('regie:pipeline.prixPropose', { numero: i + 1 })}
                >
                  <PriceInput
                    id={`affaire-offre-prix-${i}`}
                    cents={prix[i]!}
                    lang={langue}
                    onCentsChange={(v) => setPrix(prix.map((p, index) => (index === i ? v : p)))}
                  />
                </Field>
              ))
            : null}
          <Field htmlFor="affaire-terme" label={t('regie:pipeline.terme')}>
            <Input
              id="affaire-terme"
              type="date"
              value={terme}
              onChange={(e) => setTerme(e.target.value)}
            />
          </Field>
          {cible.isError ? (
            <p role="alert">
              {estApiError(cible.error) ? cible.error.message : t('commun:erreurReseau')}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </div>
  );
}
function LigneAffaire({
  ligne,
  numero,
  devise,
}: {
  ligne: AffairePresentee['lignes'][number];
  numero: number;
  devise: AffairePresentee['devise'];
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const oeuvre = useOeuvre(
    galerieSlug,
    galerieALaction(galerie, 'artworks:read') ? ligne.oeuvreId : null,
  );
  const exemplaire = oeuvre.data?.editions.find((e) => e.id === ligne.exemplaireId);
  return (
    <li>
      {oeuvre.data ? (
        <Link to={`/${galerieSlug}/oeuvres/${ligne.oeuvreId}`}>{oeuvre.data.oeuvre.titre}</Link>
      ) : (
        t('pipeline.ligne', { numero })
      )}{' '}
      {exemplaire?.numero}
      {ligne.montantCentimes !== undefined ? (
        <Price
          cents={ligne.montantCentimes}
          currency={devise}
          lang={i18n.language === 'en' ? 'en' : 'fr'}
        />
      ) : null}
    </li>
  );
}
